import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaLink,
  FaPaperclip,
  FaTrash,
  FaSignature,
} from 'react-icons/fa';
import Cookies from 'js-cookie';
import EmailSignatureModal from './EmailSignatureModal';
import {
  getLocalEmailSignature,
  isBlankSignatureHtml,
  setLocalEmailSignature,
  wrapSignatureHtml,
} from './emailSignatureStorage';
import '../styles/WriteEmailModal.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useProfileBookingId } from '../ProfileBookingContext';

const GENERATE_UPLOAD_URLS = `
  mutation GenerateUploadUrls($files: [Fileupload!]!) {
    generateAwsURlForUploadEmailFIle(files: $files) {
      key
      url
    }
  }
`;

const SEND_CUSTOM_MAIL = `
  mutation SendCustomMail(
    $subject: String!
    $info: String!
    $bookingId: String!
    $paymentLink: Boolean!
    $fileKey: String
    $to: String!
    $cc: String
  ) {
    sendCustomMail(
      subject: $subject
      info: $info
      bookingId: $bookingId
      paymentLink: $paymentLink
      fileKey: $fileKey
      to: $to
      cc: $cc
    )
  }
`;

const GET_ALL_EMAIL = `
  query GetAllEmail($bookingId: String!) {
    getAllEmail(bookingId: $bookingId)
  }
`;

const GET_MY_EMAIL_SIGNATURE = `
  query GetMyEmailSignature {
    getMyEmailSignature
  }
`;

const DRAWN_IMG_REGEX =
  /<img\b[^>]*data-sas-drawn-signature=["']?1["']?[^>]*>/gi;

function stripDrawnFromHtml(html = '') {
  return String(html).replace(DRAWN_IMG_REGEX, '');
}

function extractDrawnFromHtml(html = '') {
  const matches = String(html).match(DRAWN_IMG_REGEX);
  return matches ? matches.join('') : '';
}

export default function WriteEmailModal({ onClose, recipientEmail }) {
  const [recipients, setRecipients] = useState(recipientEmail || '');
  const [subject, setSubject] = useState('');
  const [, setEmailBody] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [signature, setSignature] = useState('');
  // What we will insert/send. If both exist you can choose (Text / Drawn / Both).
  const [signatureChoice, setSignatureChoice] = useState('both');
  const [showSignaturePicker, setShowSignaturePicker] = useState(false);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [cc, setCc] = useState('');
  const bookingId = useProfileBookingId();
  const [emailOptions, setEmailOptions] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [sending, setSending] = useState(false);

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const userId = Cookies.get('userId');
    const local = getLocalEmailSignature(userId);
    setSignature(local);

    (async () => {
      try {
        const data = await graphqlRequest(GET_MY_EMAIL_SIGNATURE);
        if (cancelled) return;
        const remote = data?.getMyEmailSignature || '';
        if (!isBlankSignatureHtml(remote)) {
          setLocalEmailSignature(remote, userId);
          setSignature(remote);
        }
      } catch (err) {
        console.error('Failed to load saved signature', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchEmails = async () => {
      if (!bookingId) return;
      try {
        setLoadingEmails(true);
        const data = await graphqlRequest(GET_ALL_EMAIL, { bookingId });
        setEmailOptions(data?.getAllEmail || []);
      } catch (err) {
        console.error('Failed to fetch emails', err);
      } finally {
        setLoadingEmails(false);
      }
    };
    fetchEmails();
  }, [bookingId]);

  const toggleFormat = (command, key) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, null);
    if (key) setActiveFormats((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleInsertLink = () => {
    if (!editorRef.current) return;
    const url = prompt('Enter the link URL:');
    if (!url) return;
    const text = prompt('Enter link text:', url) || url;
    editorRef.current.focus();
    document.execCommand('createLink', false, url);

    const sel = window.getSelection();
    if (sel?.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const a = range.commonAncestorContainer.parentElement;
      if (a && a.tagName === 'A') a.textContent = text;
    }
  };

  const applySignatureChoice = (choice) => {
    if (!editorRef.current) return;
    const textSignatureHtml = stripDrawnFromHtml(signature);
    const drawnSignatureHtml = extractDrawnFromHtml(signature);

    setSignatureChoice(choice);
    setShowSignaturePicker(false);

    const current = editorRef.current.innerHTML || '';
    const withoutOld = current.replace(
      /<div[^>]*data-sas-email-signature=["']?1["']?[^>]*>[\s\S]*?<\/div>/gi,
      '',
    );

    const contentToInsert =
      choice === 'both'
        ? signature
        : choice === 'text'
          ? textSignatureHtml
          : drawnSignatureHtml;

    const sigHTML = wrapSignatureHtml(contentToInsert);
    const cleaned = withoutOld.replace(/(<br\s*\/?>|\s)+$/i, '').trim();
    editorRef.current.innerHTML = cleaned
      ? `${cleaned}<br><br>${sigHTML}`
      : sigHTML;

    setEmailBody(editorRef.current.innerHTML);

    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    editorRef.current.focus();
  };

  const insertSignature = () => {
    if (!editorRef.current) return;

    const textSignatureHtml = stripDrawnFromHtml(signature);
    const drawnSignatureHtml = extractDrawnFromHtml(signature);
    const hasTextSignature = !isBlankSignatureHtml(textSignatureHtml);
    const hasDrawnSignature = !isBlankSignatureHtml(drawnSignatureHtml);

    if (!hasTextSignature && !hasDrawnSignature) {
      setShowSignatureModal(true);
      return;
    }

    if (hasTextSignature && hasDrawnSignature) {
      // Show the inline picker instead of a prompt
      setShowSignaturePicker(true);
      return;
    }

    // Only one type exists — insert directly
    applySignatureChoice(hasTextSignature ? 'text' : 'drawn');
  };

  const handleAttachFile = () => fileInputRef.current?.click();
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) setAttachedFiles((prev) => [...prev, ...files]);
    e.target.value = '';
    editorRef.current?.focus();
  };

  const handleDelete = () => {
    if (editorRef.current) editorRef.current.innerHTML = '';
    setAttachedFiles([]);
    setEmailBody('');
  };

  const handleEditorChange = () => {
    if (editorRef.current) setEmailBody(editorRef.current.innerHTML);
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('write-email-overlay')) onClose();
  };

  const handleSend = async () => {
    const bodyHTML = editorRef.current?.innerHTML || '';
    let finalHTML = bodyHTML;

    const textSignatureHtml = stripDrawnFromHtml(signature);
    const drawnSignatureHtml = extractDrawnFromHtml(signature);
    const hasTextSignature = !isBlankSignatureHtml(textSignatureHtml);
    const hasDrawnSignature = !isBlankSignatureHtml(drawnSignatureHtml);

    let resolvedChoice = 'none';
    if (hasTextSignature && hasDrawnSignature) {
      resolvedChoice =
        signatureChoice === 'text'
          ? 'text'
          : signatureChoice === 'drawn'
            ? 'drawn'
            : 'both';
    } else if (hasTextSignature) {
      resolvedChoice = 'text';
    } else if (hasDrawnSignature) {
      resolvedChoice = 'drawn';
    }

    const contentToSend =
      resolvedChoice === 'both'
        ? signature
        : resolvedChoice === 'text'
          ? textSignatureHtml
          : drawnSignatureHtml;

    if (
      includeSignature &&
      resolvedChoice !== 'none' &&
      !/data-sas-email-signature/i.test(bodyHTML)
    ) {
      finalHTML = `${bodyHTML}<br><br>${wrapSignatureHtml(contentToSend)}`;
    }

    let fileKeyToSend = null;

    try {
      setSending(true);
      if (!recipients) {
        toast.error('Please select a recipient email');
        return;
      }
      if (!subject.trim()) {
        toast.error('Please enter a subject');
        return;
      }
      if (attachedFiles.length > 0) {
        const res = await graphqlRequest(GENERATE_UPLOAD_URLS, {
          files: attachedFiles.map((f) => ({
            fileName: f.name,
            contentType: f.type || 'application/octet-stream',
          })),
        });

        const urls = res.generateAwsURlForUploadEmailFIle;
        const uploadedKeys = [];

        await Promise.all(
          urls.map((item, idx) =>
            fetch(item.url, {
              method: 'PUT',
              headers: {
                'Content-Type':
                  attachedFiles[idx].type || 'application/octet-stream',
              },
              body: attachedFiles[idx],
            }).then(() => uploadedKeys.push(item.key)),
          ),
        );

        fileKeyToSend = uploadedKeys.join(',');
      }

      await graphqlRequest(SEND_CUSTOM_MAIL, {
        subject,
        bookingId,
        to: recipients,
        info: finalHTML,
        paymentLink: false,
        fileKey: fileKeyToSend,
        cc: cc.trim() || null,
      });

      toast.success('Email sent successfully!');
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  const textSignatureHtmlForPreview = stripDrawnFromHtml(signature);
  const drawnSignatureHtmlForPreview = extractDrawnFromHtml(signature);
  const hasTextSignatureForPreview = !isBlankSignatureHtml(
    textSignatureHtmlForPreview,
  );
  const hasDrawnSignatureForPreview = !isBlankSignatureHtml(
    drawnSignatureHtmlForPreview,
  );

  const resolvedSignatureChoice =
    hasTextSignatureForPreview && hasDrawnSignatureForPreview
      ? signatureChoice === 'text'
        ? 'text'
        : signatureChoice === 'drawn'
          ? 'drawn'
          : 'both'
      : hasTextSignatureForPreview
        ? 'text'
        : hasDrawnSignatureForPreview
          ? 'drawn'
          : 'none';

  const contentPreviewHtml =
    resolvedSignatureChoice === 'both'
      ? signature
      : resolvedSignatureChoice === 'text'
        ? textSignatureHtmlForPreview
        : resolvedSignatureChoice === 'drawn'
          ? drawnSignatureHtmlForPreview
          : '';

  return (
    <div className="write-email-overlay" onClick={handleOverlayClick}>
      <div className="write-email-modal">
        <div className="write-email-header">
          <span className="write-email-title">New Message</span>
          <button
            type="button"
            className="write-email-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="write-email-body">
          <div className="write-email-field write-email-field-boxed">
            <label className="write-email-label">To</label>
            <select
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              className="write-email-select-boxed"
              disabled={loadingEmails}
            >
              <option value="">
                {loadingEmails ? 'Loading emails...' : 'Select recipient email'}
              </option>
              {emailOptions.map((email, index) => (
                <option key={index} value={email}>
                  {email}
                </option>
              ))}
            </select>
          </div>

          <div className="write-email-field write-email-field-boxed">
            <label className="write-email-label">CC</label>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              className="write-email-input write-email-input-boxed"
              placeholder="Comma-separated CC emails"
            />
          </div>

          <div className="write-email-field write-email-field-boxed">
            <label className="write-email-label">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="write-email-input write-email-input-boxed"
              placeholder="Email subject"
            />
          </div>

          <div className="write-email-body-field">
            <div className="write-email-body-header">
              <label className="write-email-signature-toggle">
                <input
                  type="checkbox"
                  checked={includeSignature}
                  onChange={(e) => setIncludeSignature(e.target.checked)}
                />{' '}
                Include signature
              </label>
              <div className="write-email-signature-actions">
                <button
                  type="button"
                  className="write-email-signature-btn"
                  onClick={insertSignature}
                >
                  <FaSignature /> Insert Signature
                </button>
                <button
                  type="button"
                  className="write-email-signature-settings-btn"
                  onClick={() => setShowSignatureModal(true)}
                >
                  Edit Signature
                </button>
              </div>
            </div>

            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              className="write-email-textarea"
              onInput={handleEditorChange}
              data-placeholder="Compose your email..."
            />

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
              onChange={handleFileChange}
            />

            {showSignaturePicker && (
              <div className="write-email-signature-preview">
                <div className="write-email-signature-divider" />

                {hasTextSignatureForPreview && (
                  <div className="write-email-sig-choice-block">
                    <div className="write-email-sig-choice-header">
                      <span>Text Signature Preview</span>
                      <button
                        type="button"
                        className="write-email-sig-choice-use-btn"
                        onClick={() => applySignatureChoice('text')}
                      >
                        Use Text Signature
                      </button>
                    </div>
                    <div
                      className="write-email-signature-content"
                      dangerouslySetInnerHTML={{
                        __html: wrapSignatureHtml(textSignatureHtmlForPreview),
                      }}
                    />
                  </div>
                )}

                {hasTextSignatureForPreview && hasDrawnSignatureForPreview && (
                  <div className="write-email-signature-divider" />
                )}

                {hasDrawnSignatureForPreview && (
                  <div className="write-email-sig-choice-block">
                    <div className="write-email-sig-choice-header">
                      <span>Drawn Signature Preview</span>
                      <button
                        type="button"
                        className="write-email-sig-choice-use-btn"
                        onClick={() => applySignatureChoice('drawn')}
                      >
                        Use Drawn Signature
                      </button>
                    </div>
                    <div
                      className="write-email-signature-content"
                      dangerouslySetInnerHTML={{
                        __html: wrapSignatureHtml(drawnSignatureHtmlForPreview),
                      }}
                    />
                  </div>
                )}

                <button
                  type="button"
                  className="write-email-sig-picker-cancel"
                  onClick={() => setShowSignaturePicker(false)}
                >
                  Cancel
                </button>
              </div>
            )}

            {!showSignaturePicker && includeSignature && resolvedSignatureChoice !== 'none' ? (
              <div className="write-email-signature-preview">
                <div className="write-email-signature-divider" />
                <div
                  className="write-email-signature-content"
                  dangerouslySetInnerHTML={{
                    __html: wrapSignatureHtml(contentPreviewHtml),
                  }}
                />
              </div>
            ) : !showSignaturePicker && includeSignature ? (
              <div className="write-email-signature-preview">
                <div className="write-email-signature-content">
                  No saved signature yet — click Edit Signature to create one.
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="write-email-footer">
          <button
            type="button"
            onClick={handleSend}
            className="write-email-send-btn"
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>

          <div className="write-email-toolbar">
            <button
              type="button"
              className={activeFormats.bold ? 'active' : ''}
              onClick={() => toggleFormat('bold', 'bold')}
            >
              <FaBold />
            </button>
            <button
              type="button"
              className={activeFormats.italic ? 'active' : ''}
              onClick={() => toggleFormat('italic', 'italic')}
            >
              <FaItalic />
            </button>
            <button
              type="button"
              className={activeFormats.underline ? 'active' : ''}
              onClick={() => toggleFormat('underline', 'underline')}
            >
              <FaUnderline />
            </button>
            <button
              type="button"
              onClick={() => toggleFormat('insertUnorderedList', null)}
            >
              <FaListUl />
            </button>
            <button type="button" onClick={handleInsertLink}>
              <FaLink />
            </button>
            <button type="button" onClick={handleAttachFile}>
              <FaPaperclip />
            </button>
            <button type="button" onClick={handleDelete}>
              <FaTrash />
            </button>
          </div>

          {attachedFiles.length > 0 && (
            <div className="write-email-attachments">
              <strong>Attachments:</strong>
              {attachedFiles.map((f, i) => (
                <span key={i}>
                  {f.name}
                  <button
                    type="button"
                    onClick={() =>
                      setAttachedFiles((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {showSignatureModal ? (
        <EmailSignatureModal
          onClose={() => setShowSignatureModal(false)}
          onSaved={(html) => setSignature(html)}
        />
      ) : null}
    </div>
  );
}
